import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import Collapse from '@material-ui/core/Collapse';
import Radio from '@material-ui/core/Radio';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import styled, { keyframes } from 'styled-components';
import { connect } from 'react-redux';
import { createForm, formShape } from 'rc-form';
import { colors } from '../../../constants/colors';
import { fontSize, fontFamily } from '../../../constants/text';
import { biggerThanOrEqualTo, smallerThanOrEqualTo } from '../../../core/util/styledComponents';
import TextField from '../../SolvPatternLibrary/TextField';
import Button, { ButtonVariations } from '../../SolvPatternLibrary/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { CDP_REVIEW_REPORT_SUBMITTED } from '../../../constants';

export const REPORT_REVIEW_MODAL = 'reportReviewModal';
const NAME_FIELD = 'name';
const EMAIL_FIELD = 'email';
const REASON_FIELD = 'reason';
const OTHER_FIELD = 'other';

const REPORT_REASONS = [
  'This review contains hateful, violent, or inappropriate content',
  'This review contains advertising or spam',
  'This review contains conflict of interest',
  'Other',
];

const SmoothDialog = styled(Dialog)`
  -webkit-font-smoothing: antialiased;
  & .paper-root {
    overflow-y: unset;
  }
`;

const FormContainer = styled.div`
  margin: 0 auto;
  max-width: 600px;
  background-color: white;
  padding: 20px 40px;

  ${smallerThanOrEqualTo.md`
    padding: 20px;
  `}
`;

const Close = styled.div`
  width: 25px;
  height: 25px;
  position: relative;
  cursor: pointer;
  margin: 0 0 -10px auto;
  ${smallerThanOrEqualTo.md`
    margin 0 0 0 auto;
  `}

  &:before {
    content: '';
    left: 50%;
    height: 100%;
    width: 1px;
    position: absolute;
    transform: rotate(45deg);
    background-color: ${colors.squid};
  }

  &:after {
    content: '';
    left: 50%;
    height: 100%;
    width: 1px;
    position: absolute;
    transform: rotate(-45deg);
    background-color: ${colors.squid};
  }
`;

const Header = styled.div`
  font-size: ${fontSize.hellaLarge};
  font-family: ${fontFamily.bold};
  color: ${colors.squid};
  text-align: center;
  margin: 0 auto;

  ${smallerThanOrEqualTo.md`
    font-size: ${fontSize.extraLarge}
  `}
`;

const ActionContainer = styled.div`
  margin-top: 15px;
  width: 100%;
  display: flex;
  justify-content: flex-end;

  ${smallerThanOrEqualTo.md`
    flex-direction: column-reverse;
  `}
`;

const CancelButton = styled(Button)`
  color: ${colors.crunchBerry};
  border-color: ${colors.crunchBerry};

  ${smallerThanOrEqualTo.md`
    width: 100%;
  `}
`;

const RadioAction = styled(FormControlLabel)`
  display: flex;
`;

const SubmitButton = styled(CancelButton)`
  color: ${colors.white};
  margin: 0 0 15px;

  ${biggerThanOrEqualTo.md`
    margin: 0 0 0 15px;
  `}
`;

const fill = keyframes`
  from {
    transform: scaleX(0);
  } to {
    transform: scaleX(1);
  }
`;

const ThanksForFeedback = styled.div`
  font-family: ${fontFamily.book};
  font-size: ${fontSize.large};
  color: ${colors.greenHope};
  position: relative;
  margin: 100px auto 140px;
  display: inline-block;

  &:after {
    content: '';
    animation: ${fill} 4s linear 1;
    background-color: ${colors.greenHope};
    display: block;
    margin-top: 10px;
    height: 6px;
    width: 100%;
    transform-origin: left;
  }
`;

const CenteredCollapse = styled(Collapse)`
  text-align: center;
`;

class ReportReviewModal extends PureComponent {
  static propTypes = {
    submitInfo: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    form: formShape.isRequired,
    fullScreen: PropTypes.bool,
    solvRatings: PropTypes.array,
    open: PropTypes.bool,
  };

  state = { submitted: false };

  componentWillUnmount() {
    clearTimeout(this.submitTimeout);
  }

  onChange = ({ target: { id, value } }) => this.props.form.setFieldsValue({ [id]: value });

  getErrors = () => ({
    nameError: this.props.form.getFieldError(NAME_FIELD),
    emailError: this.props.form.getFieldError(EMAIL_FIELD),
    reportError: this.props.form.getFieldError(REASON_FIELD),
  });

  nameField = {
    ...this.props.form.getFieldProps(NAME_FIELD, {
      validateTrigger: 'onBlur',
      rules: [{ required: true, message: 'required' }],
    }),
    id: NAME_FIELD,
    label: 'Name',
    onChange: this.onChange,
  };

  emailField = {
    ...this.props.form.getFieldProps(EMAIL_FIELD, {
      validateTrigger: 'onBlur',
      rules: [
        { required: true, message: 'required' },
        { type: 'email', message: 'must be an email' },
      ],
    }),
    id: EMAIL_FIELD,
    type: 'email',
    label: 'Email',
    onChange: this.onChange,
  };

  reasonField = {
    ...this.props.form.getFieldProps(NAME_FIELD, {
      validateTrigger: 'onBlur',
      rules: [{ required: true, message: 'required' }],
    }),
  };

  otherField = {
    ...this.props.form.getFieldProps(OTHER_FIELD),
    onChange: this.onChange,
    id: OTHER_FIELD,
    label: 'enter reason',
  };

  handleSubmit = e => {
    e.preventDefault();
    this.props.form.validateFields(this.validateAndSubmit);
  };

  validateAndSubmit = errors => {
    if (errors) return;
    const { form, submitInfo, solvRatings } = this.props;
    const formData = form.getFieldsValue();
    const reasonValue =
      formData[REASON_FIELD] === 'Other' ? formData[OTHER_FIELD] : formData[REASON_FIELD];
    const filtered = solvRatings.filter(rating => rating.currentlyFlagged);

    if (Array.isArray(filtered)) {
      const reportedRating = filtered[0];
      submitInfo({
        name: formData[NAME_FIELD],
        email: formData[EMAIL_FIELD],
        reason: reasonValue,
        ...reportedRating,
      });
    }

    this.setState({ submitted: true });
    this.submitTimeout = setTimeout(() => this.setState({ submitted: false }), 4500);
  };

  helperNode = error => Array.isArray(error) && error[0];

  render = () => {
    const {
      fullScreen,
      open,
      onClose,
      form: { getFieldProps, getFieldValue },
    } = this.props;
    const { nameError, emailError } = this.getErrors();
    const { submitted } = this.state;

    return (
      <SmoothDialog
        open={open}
        maxWidth="md"
        onClose={onClose}
        fullScreen={fullScreen}
        PaperProps={{ classes: { root: 'paper-root' } }}
      >
        <FormContainer>
          <Close onClick={onClose} />
          <Header>Report Inaccurate Information</Header>
          <Collapse in={!submitted} timeout="auto">
            <form onSubmit={this.handleSubmit}>
              <TextField
                fullWidth
                error={Boolean(nameError)}
                helperText={this.helperNode(nameError)}
                {...this.nameField}
              />
              <TextField
                fullWidth
                error={Boolean(emailError)}
                helperText={this.helperNode(emailError)}
                {...this.emailField}
              />
              {REPORT_REASONS.map(r => (
                <RadioAction
                  key={r}
                  label={r}
                  value={r}
                  control={
                    <Radio
                      {...getFieldProps(r)}
                      onChange={() =>
                        this.onChange({
                          target: { id: REASON_FIELD, value: r },
                        })
                      }
                      checked={getFieldValue(REASON_FIELD) === r}
                    />
                  }
                />
              ))}
              <Collapse in={getFieldValue(REASON_FIELD) === 'Other'}>
                <TextField fullWidth {...this.otherField} />
              </Collapse>
              <ActionContainer>
                <CancelButton variation={ButtonVariations.tertiary} onClick={onClose}>
                  Cancel
                </CancelButton>
                <SubmitButton variation={ButtonVariations.pinkPrimary} type="submit">
                  Submit
                </SubmitButton>
              </ActionContainer>
            </form>
          </Collapse>
          <CenteredCollapse in={submitted} timeout="auto" mountOnEnter unmountOnExit>
            <ThanksForFeedback>Thanks for the report!</ThanksForFeedback>
          </CenteredCollapse>
        </FormContainer>
      </SmoothDialog>
    );
  };
}

const mapStateToProps = state => ({
  solvRatings: state.solvRatings,
});

const mapDispatchToProps = dispatch => ({
  submitInfo: value => dispatch({ type: CDP_REVIEW_REPORT_SUBMITTED, value }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withMobileDialog({ breakpoint: 'xs' })(createForm()(ReportReviewModal)));
